// version.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class VersionService {
  constructor(private http: HttpClient) {}

  loadVersion(): Promise<void> {
    // Agregar timestamp para evitar cache en la petición
    const timestamp = new Date().getTime();
    return this.http.get<{ version: string }>(`/assets/version.json?v=${timestamp}`)
      .toPromise()
      .then(data => {
        const currentVersion = data?.version || 'unknown version';
        const savedVersion = localStorage.getItem('appVersion');

        if (savedVersion && savedVersion !== currentVersion) {
          // 🔥 versión cambió → limpiar todo como Ctrl+Shift+R
          // Establecer la nueva versión antes de limpiar para que se mantenga después del reload
          localStorage.setItem('appVersion', currentVersion);
          this.clearOldState();
          // No continuar después de clearOldState porque recarga la página
          return;
        }

        // Si no hay cambio de versión, solo actualizar
        localStorage.setItem('appVersion', currentVersion);
      })
      .catch(error => {
        console.error('Error al cargar la versión:', error);
      });
  }

  private async clearBrowserCache(): Promise<void> {
    // Limpiar Service Worker cache si existe
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      } catch (error) {
        console.warn('Error al desregistrar Service Workers:', error);
      }
    }

    // Limpiar Cache API (incluye memory cache en algunos navegadores)
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      } catch (error) {
        console.warn('Error al limpiar Cache API:', error);
      }
    }

    // Intentar invalidar el caché del navegador
    // Nota: No podemos limpiar el memory cache directamente desde JS por seguridad,
    // pero podemos forzar al navegador a recargar usando técnicas específicas
    try {
      // Forzar invalidación de caché haciendo peticiones con no-cache
      const baseUrl = window.location.origin + window.location.pathname;
      await fetch(baseUrl, {
        method: 'HEAD',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
    } catch (error) {
      // Ignorar errores de fetch
    }
  }

  private clearOldState() {
    // Limpiar todo como un Ctrl+Shift+R (hard refresh)
    
    // 1. Guardar la nueva versión antes de limpiar
    const newVersion = localStorage.getItem('appVersion');
    
    // 2. Limpiar localStorage completamente
    localStorage.clear();
    
    // 3. Restaurar solo la nueva versión
    if (newVersion) {
      localStorage.setItem('appVersion', newVersion);
    }
    
    // 4. Limpiar sessionStorage completamente
    sessionStorage.clear();
    
    // 5. Limpiar todas las cookies (dominio actual y subdominios)
    const domain = window.location.hostname;
    const cookies = document.cookie.split(';');
    
    cookies.forEach(c => {
      const eqPos = c.indexOf('=');
      const name = eqPos > -1 ? c.substring(0, eqPos).trim() : c.trim();
      
      if (name) {
        // Eliminar cookie del path raíz
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        
        // Eliminar cookie del dominio actual
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
        
        // Eliminar cookie del dominio base (para subdominios)
        if (domain.indexOf('.') !== -1) {
          const baseDomain = '.' + domain.split('.').slice(-2).join('.');
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${baseDomain}`;
        }
        
        // Intentar eliminar sin dominio (algunos navegadores)
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=`;
      }
    });
    
    // 6. Forzar recarga completa sin cache (equivalente a Ctrl+Shift+R)
    // Limpiar Service Worker y Cache API antes de recargar
    this.clearBrowserCache().then(() => {
      // Usar un pequeño delay para asegurar que todo se guarde antes de recargar
      setTimeout(() => {
        // Intentar invalidar el caché de memoria modificando los scripts dinámicamente
        // Esto fuerza al navegador a recargar los scripts desde el servidor
        const scripts = document.querySelectorAll('script[src]');
        const timestamp = new Date().getTime();
        scripts.forEach((scriptElement) => {
          const script = scriptElement as HTMLScriptElement;
          if (script.src && !script.src.includes('?nocache=')) {
            const separator = script.src.includes('?') ? '&' : '?';
            script.src = script.src + separator + 'nocache=' + timestamp;
          }
        });
        
        // Obtener la URL base
        const baseUrl = window.location.origin + window.location.pathname;
        const randomId = Math.random().toString(36).substring(7);
        
        // Crear una nueva URL con parámetros únicos para forzar recarga sin cache
        // Los parámetros de query fuerzan al navegador a tratar la URL como nueva
        const newUrl = baseUrl + '?nocache=' + timestamp + '&r=' + randomId;
        
        // Usar replace para evitar agregar al historial
        // Esto fuerza al navegador a recargar todos los recursos sin usar cache
        window.location.replace(newUrl);
      }, 500);
    });
  }
}
